// Sort comparator for the AI Usage context table. Standalone (not in src/utils/index.js,
// which contains JSX and breaks Vitest imports — see frontend CLAUDE.md).
//
// Null-safe and type-aware:
// - round_id / task_type / resolved labels can be null/undefined — pushed to the end
//   regardless of sort direction, so "unknown" rows don't jump around when toggling asc/desc.
// - cost is NUMERIC(14,8) and arrives as a STRING in JSON (the page already does
//   parseFloat(context.cost) at render time) — must parseFloat here too, or "$9.00" sorts
//   above "$10.00" (string comparison).
// - calls / total_tokens / thread_count / errors are plain ints already.
// - first_call / last_call / calls can never be null: get_usage_by_context only returns
//   contexts with >=1 underlying llm_usage row (COUNT(*)/MIN/MAX over >=1 row) — no guard
//   needed for those specifically, but the shared null-guard below is harmless for them too.

export const SORT_DIRECTIONS = Object.freeze({
  ASC: "asc",
  DESC: "desc",
  NONE: "none",
});

// Columns whose value must be parseFloat'd (arrives as a string or should be compared
// numerically) rather than compared as-is.
const NUMERIC_COLUMNS = new Set(["cost", "total_tokens", "calls", "thread_count", "errors"]);

/**
 * Cycle NONE -> ASC -> DESC -> NONE, matching the app-wide click-to-sort convention
 * (see components/DataTable/DataTable.jsx's handleColumnHeaderClick).
 */
export function nextSortDirection(current) {
  switch (current) {
    case SORT_DIRECTIONS.NONE:
    case undefined:
      return SORT_DIRECTIONS.ASC;
    case SORT_DIRECTIONS.ASC:
      return SORT_DIRECTIONS.DESC;
    case SORT_DIRECTIONS.DESC:
    default:
      return SORT_DIRECTIONS.NONE;
  }
}

function extractValue(row, column) {
  if (column === "label") return row._label; // resolved display label, injected by the page
  return row[column];
}

/**
 * Sort a copy of `rows` by `column` in `direction`. Returns the original array (unsorted)
 * when direction is NONE, so callers can fall back to backend order.
 */
export function sortContexts(rows, column, direction) {
  if (!column || !direction || direction === SORT_DIRECTIONS.NONE) return rows;
  const sign = direction === SORT_DIRECTIONS.DESC ? -1 : 1;
  const isNumeric = NUMERIC_COLUMNS.has(column);

  return [...rows].sort((a, b) => {
    let va = extractValue(a, column);
    let vb = extractValue(b, column);

    const aNull = va === null || va === undefined || va === "";
    const bNull = vb === null || vb === undefined || vb === "";
    if (aNull && bNull) return 0;
    if (aNull) return 1; // nulls always last, regardless of direction
    if (bNull) return -1;

    if (isNumeric) {
      va = parseFloat(va) || 0;
      vb = parseFloat(vb) || 0;
      return (va - vb) * sign;
    }

    // Dates (first_call/last_call) compare correctly as ISO strings too, but be explicit.
    if (column === "first_call" || column === "last_call") {
      return (new Date(va) - new Date(vb)) * sign;
    }

    return String(va).localeCompare(String(vb)) * sign;
  });
}
